import { useState } from 'react';
import { UserIcon } from 'lucide-react';
import type { Profile } from '@/types/database.types';
import FollowButton from './FollowButton';

interface UserProfileCardProps {
  profile: Profile;
  showFollowButton?: boolean;
  onFollowChange?: () => void;
}

const DEFAULT_AVATAR = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';

export default function UserProfileCard({ profile, showFollowButton = true, onFollowChange }: UserProfileCardProps) {
  const [imageError, setImageError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(profile.avatar_url || DEFAULT_AVATAR);

  const handleImageError = () => {
    setImageError(true);
    setAvatarUrl(DEFAULT_AVATAR);
  };

  // Reset image error state when profile or avatar URL changes
  if (profile.avatar_url && profile.avatar_url !== avatarUrl && !imageError) {
    setAvatarUrl(profile.avatar_url);
    setImageError(false);
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={profile.username}
              className="h-16 w-16 rounded-full object-cover ring-2 ring-gray-100"
              onError={handleImageError}
            />
          ) : (
            <div className="h-16 w-16 rounded-full bg-gray-200 flex items-center justify-center ring-2 ring-gray-100">
              <UserIcon className="h-8 w-8 text-gray-500" />
            </div>
          )}
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{profile.username}</h3>
            <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
              <span>{profile.followers_count} followers</span>
              <span>{profile.following_count} following</span>
            </div>
          </div>
        </div>
        {showFollowButton && (
          <FollowButton
            userId={profile.id}
            isFollowing={profile.is_following}
            onFollowChange={onFollowChange}
          />
        )}
      </div>
    </div>
  );
}